import { ChevronRight, QrCode, Utensils } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import useTableOrder from '../../hooks/order/useTableOrder';
import useMobileApp from '../../hooks/mobile-app/useMobileApp';
import useAppMethods from '../../hooks/utility/useAppMethods';
import useOrderFlow from '../../hooks/order/useOrderFlow';
import { CompanyDetailsInterface } from '../../services/exports/Interfaces';
import { StoreInterface } from '../../store/types';
import { selectTableNotice } from '../../store/slices/OrderMethodSlice';
import { TABLE_LINK_NOTICE_PARAM, TableLinkNotice, companyHasOnSite, formatTableName } from '../../utils/on-site';

// The table was refused or table ordering stopped: the menu's note says so, and this row would contradict it.
const HIDDEN_FOR_NOTICES: TableLinkNotice[] = [TableLinkNotice.TableUnavailable, TableLinkNotice.TableOrderingOff];

/**
 * At the top of the order-method modal, on every tab: the way back to the table a guest scanned, or,
 * at a restaurant that takes table orders, how to start one. Only a table's QR code starts table
 * ordering, so there is no table to pick. The app doesn't offer table ordering (its QR codes open the
 * web shop).
 */
interface Props {
  /** The location the guest is picking in the modal, which may not be the one the shop shows yet. */
  company?: CompanyDetailsInterface | null;
}

export default function EatingHereRow({ company: pickedCompany }: Props) {
  const { t } = useTranslation(null, { keyPrefix: 'Components:OrderType:TableOrdering' });
  const storedCompany = useSelector((state: StoreInterface) => state.initialData.company);
  const company = pickedCompany ?? storedCompany;
  const tableNotice = useSelector(selectTableNotice);
  const [searchParams] = useSearchParams();
  const { table, returnToTable } = useTableOrder();
  const { isMobileApp } = useMobileApp();
  const { toggleOrderMethodModal } = useAppMethods();
  const { setOrderMethodInfoInfoComplete } = useOrderFlow();

  // A link that just failed (the query parameter), or a table refused or table ordering stopped during
  // the visit, says why in its own note. A load that failed during the visit doesn't hide the row: the
  // way back to the earlier table, or how to start, still applies.
  if (
    isMobileApp ||
    !companyHasOnSite(company) ||
    searchParams.has(TABLE_LINK_NOTICE_PARAM) ||
    HIDDEN_FOR_NOTICES.includes(tableNotice)
  ) {
    return null;
  }

  if (table) {
    return (
      <button
        type="button"
        className={classnames(classNames.row, classNames.button)}
        onClick={() => {
          returnToTable();
          setOrderMethodInfoInfoComplete();
          toggleOrderMethodModal({ openModal: false });
        }}
        data-test="eating-here-back-to-table"
      >
        <Utensils aria-hidden="true" className={classNames.icon} />
        <span className={classNames.text}>
          <b className={classNames.title}>{t('eating_here.title')}</b>
          <br />
          {t('eating_here.back_to_table', { table: formatTableName(table.name, t('table')) })}
        </span>
        <ChevronRight aria-hidden="true" className={classNames.chevron} />
      </button>
    );
  }

  return (
    <div className={classnames(classNames.row, 'cursor-default')} data-test="eating-here-scan-code">
      <QrCode aria-hidden="true" className={classNames.icon} />
      <p className={classNames.text}>
        <b className={classNames.title}>{t('eating_here.title')}</b>
        <br />
        {t('eating_here.scan_code')}
      </p>
    </div>
  );
}

// Values from the design (Pickup sheet · Eating here).
const classNames = {
  row: 'w-full flex items-center gap-[12px] p-[12px] rounded-[12px] border border-brand-border bg-brand-card text-left text-brand-text-primary',
  button:
    'cursor-pointer transition-colors duration-[120ms] hover:bg-muted focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary',
  icon: 'w-[18px] h-[18px] shrink-0',
  text: 'flex-1 min-w-0 m-0 text-[13px] text-muted-foreground',
  title: 'text-[14px] font-bold text-brand-text-primary',
  chevron: 'w-[16px] h-[16px] shrink-0 text-muted-foreground',
};
